import { ObjectSchema, array, boolean, number, object, string } from "yup";
import {
  SubmitAudiovisualReport,
  SubmitEventReport,
  SubmitEventReportWithDataFiles,
  SubmitShowsReport,
} from "../../../../generated/data-contracts";
import {
  fileCollectionValidation,
  numberDecimal,
  numberInteger,
  requiredBoolean,
  requiredClassifierId,
  requiredPeriodValidation,
  requiredString,
  simpleString,
  stringTiming,
} from "../../../../data/yupConstants";
import { ResCallback } from "../../../../models";

export const validationSchemaAudioVisual = (
  res: ResCallback,
): ObjectSchema<SubmitAudiovisualReport> | undefined => {
  const requiredAtLeast1Of2_totalVisitors_ticketRevenue = res(
    "validation_MustFillAtLeast1Of2Fields",
    res("licensees_LicenseReports_type3_label_TicketRevenue"),
    res("licensees_LicenseReports_type3_label_TotalVisitors"),
  );
  const validationSchema: ObjectSchema<SubmitAudiovisualReport> = object({
    eventNotOccuredPortal: requiredBoolean(res),
    submitReport: requiredBoolean(res),
    rows: array().when(["eventNotOccuredPortal"], {
      is: (eventNotOccured: boolean) => eventNotOccured === false,
      then: () =>
        array().of(
          object().shape(
            {
              licenseClientAudiovisualCreationId: number(),
              nameLv: simpleString(res).max(255),
              name: requiredString(res, undefined, 255),
              manufactureYear: simpleString(res, undefined, 200),
              director: simpleString(res, undefined, 255),
              musicAuthor: simpleString(res, undefined, 255),
              totalTiming: stringTiming(res),
              ticketRevenue: numberDecimal(res, false, true).when(
                ["totalVisitors"],
                {
                  is: (totalVisitors: number | undefined) =>
                    !totalVisitors || totalVisitors === 0,
                  then: (schema) =>
                    schema.required(
                      requiredAtLeast1Of2_totalVisitors_ticketRevenue,
                    ),
                },
              ),
              totalVisitors: numberInteger(res, false, true).when(
                ["ticketRevenue"],
                {
                  is: (ticketRevenue: number | undefined) =>
                    !ticketRevenue || ticketRevenue === 0,
                  then: (schema) =>
                    schema.required(
                      requiredAtLeast1Of2_totalVisitors_ticketRevenue,
                    ),
                },
              ),
              rowVersion: string(),
            },
            [["ticketRevenue", "totalVisitors"]], //Cyclic dependency fix),
          ),
        ),
    }),
  });

  return validationSchema;
};
export const validationSchemaShows = (
  res: ResCallback,
  periodFrom: Date | undefined,
  periodTo: Date | undefined,
): ObjectSchema<SubmitShowsReport> | undefined => {
  const validationSchema: ObjectSchema<SubmitShowsReport> = object({
    eventNotOccuredPortal: requiredBoolean(res),
    submitReport: requiredBoolean(res),
    rows: array().when(["eventNotOccuredPortal"], {
      is: (eventNotOccured: boolean) => eventNotOccured === false,
      then: () =>
        array().of(
          object({
            licenseClientEventId: number(),
            creationId: requiredClassifierId(res),
            eventDate: requiredPeriodValidation(res, periodFrom, periodTo),
            grossRevenue: number().when(["withEntryFee"], {
              is: (withEntryFee: boolean | undefined) => withEntryFee,
              then: () => numberDecimal(res, true, true, true),
            }),
            soldTickets: number().when(["withEntryFee"], {
              is: (withEntryFee: boolean | undefined) => withEntryFee,
              then: () => numberInteger(res, true, true, true),
            }),
            totalInvitations: numberInteger(res, false, true),
            totalVisitorsWithoutTicket: number().when(["withEntryFee"], {
              is: (withEntryFee: boolean | undefined) => !withEntryFee,
              then: () => numberInteger(res, true, true),
            }),
            rowVersion: string(),
          }),
        ),
    }),
  });

  return validationSchema;
};
export const validationSchemaClientEvents = (
  res: ResCallback,
  periodFrom: Date | undefined,
  periodTo: Date | undefined,
): ObjectSchema<SubmitEventReport> | undefined => {
  const validationSchema: ObjectSchema<SubmitEventReport> = object({
    eventNotOccuredPortal: requiredBoolean(res),
    submitReport: requiredBoolean(res),
    rows: array().when(["eventNotOccuredPortal"], {
      is: (eventNotOccured: boolean) => eventNotOccured === false,
      then: () =>
        array().of(
          object({
            licenseClientEventId: number(),
            withEntryFee: boolean(),
            eventDate: requiredPeriodValidation(res, periodFrom, periodTo),
            eventName: requiredString(res),
            grossRevenue: number().when(["withEntryFee"], {
              is: (withEntryFee: boolean | undefined) => withEntryFee,
              then: () => numberDecimal(res, true, true, true),
            }),
            soldTickets: number().when(["withEntryFee"], {
              is: (withEntryFee: boolean | undefined) => withEntryFee,
              then: () => numberInteger(res, true, true, true),
            }),
            totalInvitations: numberInteger(res, false, true),
            totalVisitorsWithoutTicket: number().when(["withEntryFee"], {
              is: (withEntryFee: boolean | undefined) => !withEntryFee,
              then: () => numberInteger(res, true, true),
            }),
            rowVersion: string(),
          }),
        ),
    }),
  });

  return validationSchema;
};
export const validationWithDataFile = (
  res: ResCallback,
  uploadableFileTypes: string[] | undefined,
  unsupportedSymbols: string[],
  maxFileSize: number,
): ObjectSchema<SubmitEventReportWithDataFiles> | undefined => {
  const validationSchema: ObjectSchema<SubmitEventReportWithDataFiles> = object(
    {
      eventNotOccuredPortal: requiredBoolean(res),
      submitReport: requiredBoolean(res),
      rows: array().when(["eventNotOccuredPortal"], {
        is: (eventNotOccured: boolean) => eventNotOccured === false,
        then: () =>
          fileCollectionValidation(
            res,
            uploadableFileTypes,
            unsupportedSymbols,
            maxFileSize,
            true,
          ),
      }),
    },
  );

  return validationSchema;
};
